import { CategorizedThumbnails } from "../categorized/CategorizedThumbnails";
import { Paper, Typography } from "@mui/material";
import { SelectThumbnailOverlay } from "../imageOverlays/SelectThumbnailOverlay";
import { useCategorizedImageData } from "../categorized/useCategorizedImageData";
import { useTranslation } from "react-i18next";
import { useUserPreferences } from "portal/state/store";
import React, { FC, useCallback, useMemo } from "react";

const PREVIEW_THUMBNAIL_SIZE = 64;
const PREVIEW_DIMENSIONS = {
  width: PREVIEW_THUMBNAIL_SIZE,
  height: PREVIEW_THUMBNAIL_SIZE,
};

type CategoryChipPreviewBarProps = {
  chipIds: string[];
  categoryId?: string;
  ineligibleChipIds?: ReadonlySet<string>;
  selectedChipIds?: ReadonlySet<string>;
  onToggleChip?: (chipId: string, selected: boolean) => void;
};

export const CategoryChipPreviewBar: FC<CategoryChipPreviewBarProps> = ({
  chipIds,
  categoryId,
  ineligibleChipIds,
  selectedChipIds,
  onToggleChip,
}) => {
  const { t } = useTranslation();
  const userPreferences = useUserPreferences();
  const { orderedCombinedData } = useCategorizedImageData(chipIds, categoryId);
  const hasChips = chipIds.length > 0;
  const selectionEnabled = Boolean(onToggleChip && selectedChipIds);
  const skippedCount = useMemo(() => {
    if (!ineligibleChipIds || ineligibleChipIds.size === 0) {
      return 0;
    }
    const skipped = new Set<string>();
    for (const id of chipIds) {
      if (id && ineligibleChipIds.has(id)) {
        skipped.add(id);
      }
    }
    return skipped.size;
  }, [chipIds, ineligibleChipIds]);

  const renderChipOverlay = useCallback(
    (chipId: string) => {
      if (!selectionEnabled || !selectedChipIds || !onToggleChip) {
        return;
      }
      const disabled = ineligibleChipIds?.has(chipId) ?? false;
      return (
        <SelectThumbnailOverlay
          thumbnailDimensions={PREVIEW_DIMENSIONS}
          selected={!disabled && selectedChipIds.has(chipId)}
          disabled={disabled}
          onClick={(isSelected) => {
            if (!disabled) {
              onToggleChip(chipId, isSelected);
            }
          }}
        />
      );
    },
    [ineligibleChipIds, onToggleChip, selectedChipIds, selectionEnabled]
  );

  const handleClick = useCallback(
    (chipId: string) => {
      if (!onToggleChip || !selectedChipIds) {
        return;
      }
      onToggleChip(chipId, !selectedChipIds.has(chipId));
    },
    [onToggleChip, selectedChipIds]
  );

  return (
    <div className="flex flex-col gap-1">
      <Paper
        color="secondary"
        className="overflow-x-auto flex gap-1 whitespace-nowrap rounded-md p-1 items-center"
        style={{ minHeight: `${PREVIEW_THUMBNAIL_SIZE + 8}px` }}
      >
        {hasChips ? (
          <CategorizedThumbnails
            dimensions={PREVIEW_DIMENSIONS}
            images={orderedCombinedData}
            showLabels={userPreferences.categoryCollection.showLabels}
            disabledChipIds={ineligibleChipIds}
            onClick={selectionEnabled ? handleClick : undefined}
            renderChipOverlay={selectionEnabled ? renderChipOverlay : undefined}
          />
        ) : (
          <Typography
            variant="body2"
            color="text.secondary"
            className="w-full flex justify-center px-2"
          >
            {categoryId
              ? t("components.categoryCollectionProfile.actions.noImages")
              : t(
                  "components.categoryCollectionProfile.actions.selectCategory"
                )}
          </Typography>
        )}
      </Paper>
      {skippedCount > 0 && (
        <Typography variant="body2" color="text.secondary">
          {t(
            "components.categoryCollectionProfile.actions.imagesAlreadyInProfile",
            { count: skippedCount }
          )}
        </Typography>
      )}
    </div>
  );
};
